interface TempoPoint {
  label: string;
  value: number | null;
}

interface TempoChartProps {
  points: TempoPoint[];
  height?: number;
}

/**
 * The tempo-growth line. Hand-drawn SVG rather than a chart library: one line,
 * seven points, and a dependency would cost more than the forty lines below.
 *
 * Days without practice are gaps, not zeros — drawing a plunge to 0 BPM for a
 * rest day would tell the player they got worse when they simply rested.
 */
export function TempoChart({ points, height = 190 }: TempoChartProps) {
  const width = 320;
  const padding = { top: 12, right: 8, bottom: 26, left: 30 };
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const max = Math.max(80, Math.ceil((Math.max(...values, 0) + 10) / 20) * 20);
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const x = (index: number) =>
    padding.left + (points.length <= 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
  const y = (value: number) => padding.top + plotHeight - (value / max) * plotHeight;

  const drawn = points
    .map((point, index) => ({ point, index }))
    .filter((item) => item.point.value !== null);

  const path = drawn
    .map((item, i) => `${i === 0 ? 'M' : 'L'} ${x(item.index)} ${y(item.point.value as number)}`)
    .join(' ');

  const gridValues = [0, max / 4, max / 2, (max * 3) / 4, max];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label={drawn.map((item) => `${item.point.label}: ${item.point.value} BPM`).join(', ')}
    >
      {gridValues.map((value) => (
        <g key={value}>
          <line
            x1={padding.left}
            y1={y(value)}
            x2={width - padding.right}
            y2={y(value)}
            stroke="var(--border)"
            strokeDasharray="3 4"
          />
          <text
            x={padding.left - 8}
            y={y(value)}
            textAnchor="end"
            dominantBaseline="central"
            fontSize="10"
            fill="var(--ink-faint)"
          >
            {Math.round(value)}
          </text>
        </g>
      ))}

      {drawn.length > 1 && (
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" />
      )}

      {drawn.map((item) => (
        <circle
          key={item.index}
          cx={x(item.index)}
          cy={y(item.point.value as number)}
          r="4.5"
          fill="var(--surface)"
          stroke="var(--accent)"
          strokeWidth="2.5"
        />
      ))}

      {points.map((point, index) => (
        <text
          key={point.label}
          x={x(index)}
          y={height - 6}
          textAnchor="middle"
          fontSize="11"
          fill="var(--ink-muted)"
        >
          {point.label}
        </text>
      ))}
    </svg>
  );
}
